import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { CheckIcon } from '@untitledui-icons/react/line'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/base/buttons'
import { FormInput, FormTextarea, FormMdxEditor, FormAsyncCombobox } from '@/components/forms'
import { useCreatePromptTemplate } from '@/hooks/api/usePromptTemplates'
import { useSearchCustomers } from '@/hooks/api/useCustomers'
import { usePermissions } from '@/hooks/api/usePermissions'

// Validation for template names: 3-64 characters, letters, numbers, dots, underscores, hyphens
const templateNameRegex = /^[A-Za-z0-9._-]{3,64}$/

const promptTemplateSchema = z.object({
  name: z
    .string()
    .min(3, 'app.validation.template_name_min')
    .max(64, 'app.validation.template_name_max')
    .regex(
      templateNameRegex,
      'app.validation.template_name_format'
    ),
  description: z.string().optional(),
  content: z.string().min(1, 'app.validation.content_required'),
  customer_id: z.number().nullable().optional(),
})

type PromptTemplateFormValues = z.infer<typeof promptTemplateSchema>

export function PromptTemplateCreatePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { role } = usePermissions()

  const isAdmin = role === 'admin'
  const isPartner = role === 'partner'

  // Mutations
  const createTemplate = useCreatePromptTemplate()

  // Customer search state
  const [customerSearch, setCustomerSearch] = useState('')
  const { data: customers = [], isLoading: customersLoading } = useSearchCustomers(
    customerSearch,
    isAdmin || isPartner
  )

  // Convert customers to options
  const customerOptions = customers.map((c) => ({
    value: c.id,
    label: c.name,
  }))

  // Customer search handler
  const handleCustomerSearch = useCallback((term: string) => {
    setCustomerSearch(term)
  }, [])

  // Form setup
  const methods = useForm<PromptTemplateFormValues>({
    resolver: zodResolver(promptTemplateSchema),
    defaultValues: {
      name: '',
      description: '',
      content: '',
      customer_id: null,
    },
  })

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = methods

  // Handle form submission
  const onSubmit = async (data: PromptTemplateFormValues) => {
    try {
      await createTemplate.mutateAsync({
        name: data.name,
        description: data.description,
        content: data.content,
        customer_id: data.customer_id || undefined,
      })
      toast.success(t('resources.prompt_templates.create_success'))
      navigate('/prompt-templates')
    } catch (error: unknown) {
      if (error instanceof Error) {
        if (error.message.includes('409')) {
          toast.warning(t('resources.prompt_templates.name_exists'))
        } else if (error.message.includes('403')) {
          toast.error(t('resources.prompt_templates.no_permission'))
        } else if (error.message.includes('422')) {
          toast.warning(t('resources.prompt_templates.validation_error'))
        } else {
          toast.error(error.message)
        }
      } else {
        toast.error(t('resources.prompt_templates.create_error'))
      }
    }
  }

  return (
    <FormProvider {...methods}>
      <div className="flex h-full flex-col">
        <PageHeader
          title={t('resources.prompt_templates.create_title')}
          subtitle={t('resources.prompt_templates.create_subtitle')}
        />

        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-auto px-6 py-4">
          <div className="mx-auto max-w-4xl space-y-6">
            {/* Basic Info */}
            <section className="space-y-4 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t('resources.prompt_templates.fields.name')}
                </h3>
                <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
                  {t('resources.prompt_templates.descriptions.name')}
                </p>
                <FormInput
                  control={control}
                  name="name"
                  placeholder={t('resources.prompt_templates.placeholders.name')}
                  isRequired
                />
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t('resources.prompt_templates.fields.description')}
                </h3>
                <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
                  {t('resources.prompt_templates.descriptions.description')}
                </p>
                <FormTextarea
                  control={control}
                  name="description"
                  placeholder={t('resources.prompt_templates.placeholders.description')}
                  rows={2}
                />
              </div>

              {/* Customer scope (admin/partner only) */}
              {(isAdmin || isPartner) && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {isPartner
                      ? t('resources.prompt_templates.fields.customer_id')
                      : t('resources.prompt_templates.fields.customer_scope')}
                  </h3>
                  <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
                    {isAdmin
                      ? t('resources.prompt_templates.descriptions.customer_id_admin')
                      : t('resources.prompt_templates.descriptions.customer_id_partner')}
                  </p>
                  <FormAsyncCombobox
                    control={control}
                    name="customer_id"
                    options={customerOptions}
                    placeholder={
                      isAdmin
                        ? t('resources.prompt_templates.placeholders.customer_generic')
                        : t('resources.prompt_templates.placeholders.customer_select')
                    }
                    onSearch={handleCustomerSearch}
                    isLoading={customersLoading}
                    isRequired={isPartner}
                  />
                </div>
              )}
            </section>

            {/* Content */}
            <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('resources.prompt_templates.fields.content')}
              </h3>
              <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
                {t('resources.prompt_templates.descriptions.content')}
              </p>
              <FormMdxEditor
                control={control}
                name="content"
                placeholder={t('resources.prompt_templates.placeholders.content')}
                minHeight="300px"
                maxHeight="600px"
              />
            </section>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-6 dark:border-gray-700">
              <Button
                color="secondary"
                type="button"
                onPress={() => navigate('/prompt-templates')}
              >
                {t('resources.generic.cancel')}
              </Button>
              <Button color="primary" type="submit" isDisabled={isSubmitting} iconLeading={CheckIcon}>
                {isSubmitting ? t('resources.prompt_templates.creating') : t('resources.prompt_templates.create')}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </FormProvider>
  )
}

export default PromptTemplateCreatePage
